import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Text } from '@opendoor/atoms/text';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { MarketplaceDisplayBadge } from '__generated__/zeus/types';
import { corePdpLink } from 'components/helpers/links';
import type { MarketplaceFavoriteOrigin } from 'components/marketplace/helpers/__generated__/marketplace-favorites.zeus';
import { browseListingStatus } from 'components/marketplace/helpers/browseListingStatus';
import { dollarsToCents } from 'components/marketplace/helpers/money';
import {
  type MarketplaceFavoriteChangeHandler,
  useMarketplaceFavorite,
} from 'components/marketplace/helpers/useMarketplaceFavorite';
import { createMarketplaceQueryCache } from 'components/marketplace/queryCache';
import { ListingStatusBadge } from 'components/marketplace/shared/ListingStatusBadge';
import dynamic from 'next/dynamic';

import { CardLink, PropertyCard } from '../PropertyCard';
import { streetViewLocationFor, StreetViewPhoto } from '../shared/StreetViewPhoto';
import { buildAddressLines, buildPropertyPhotoUrl, convertDwellingType } from './helpers';
import { PdpSimilarListingCard } from './refresh/PdpSimilarListingCard';
import type { ShareableProperty } from './ShareModal';
import type { ListingCardData } from './types';

// SSR must render the same photo tree that hydrates; viewport checks belong on its controls.
const PropertyCardGallery = dynamic(() =>
  import('./PropertyCardGallery').then((module) => module.PropertyCardGallery),
);
const ShareModal = dynamic(() => import('./ShareModal').then((module) => module.ShareModal));

const EMPTY_PHOTO_URLS: ReadonlyArray<string> = [];

export type PhotoLoadMode = 'deferred' | 'priority';

type UseListingPhotoSourceOptions = {
  hasPhoto: boolean;
  loadMode: PhotoLoadMode;
};

const useListingPhotoSource = ({ hasPhoto, loadMode }: UseListingPhotoSourceOptions) => {
  const [shouldLoadPhoto, setShouldLoadPhoto] = useState(loadMode === 'priority');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current || shouldLoadPhoto) {
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setShouldLoadPhoto(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [shouldLoadPhoto]);

  return {
    containerRef,
    isPhotoPending: !shouldLoadPhoto && hasPhoto,
    shouldLoadPhoto,
  };
};

interface Tracking {
  utmSource?: string | null;
  utmMedium?: string | null;
  utmCampaign?: string | null;
  utmContent?: string | null;
  utmTerm?: string | null;
}

interface SimilarListingsProps {
  listings: ListingCardData[];
  title?: string | null;
  wide?: boolean;
}

interface ListingCardComponentProps {
  listing: ListingCardData;
  displayBadge?: MarketplaceDisplayBadge;
  photoLoadMode?: PhotoLoadMode;
  /** Desktop search cards opt into an interactive photo gallery; other surfaces stay static. */
  photoMode?: 'gallery' | 'static';
  contained?: boolean;
  favoriteOrigin?: MarketplaceFavoriteOrigin;
  initialFavoritePending?: boolean;
  onFavoriteChange?: MarketplaceFavoriteChangeHandler;
  variant?: 'default' | 'search-result' | 'pdp-refresh';
}

const ListingPhotoOverlay = ({ displayBadge }: { displayBadge: MarketplaceDisplayBadge }) => (
  <Row className="pointer-events-none absolute left-2 right-2 top-2 z-1">
    <ListingStatusBadge displayBadge={displayBadge} />
  </Row>
);

type ShareModalState = 'unopened' | 'open' | 'closed';

export const ListingCard: React.FC<ListingCardComponentProps & Tracking> = ({
  utmSource,
  utmMedium,
  utmCampaign,
  utmContent,
  utmTerm,
  listing,
  displayBadge,
  photoLoadMode = 'deferred',
  photoMode = 'static',
  contained = false,
  favoriteOrigin = 'LISTING_DETAILS',
  initialFavoritePending,
  onFavoriteChange,
  variant = 'default',
}) => {
  const isSearchResult = variant === 'search-result';
  const isPdpRefresh = variant === 'pdp-refresh';
  const [shareModalState, setShareModalState] = useState<ShareModalState>('unopened');
  const { isFavoriteLoading, isLiked, onToggleFavorite } = useMarketplaceFavorite({
    addressId: listing.addressToken,
    origin: favoriteOrigin,
    initialIsLiked: listing.isLiked,
    initialFavoritePending,
    onFavoriteChange,
    priceCents: listing.listPrice == null ? null : dollarsToCents(listing.listPrice),
  });

  const { containerRef, isPhotoPending, shouldLoadPhoto } = useListingPhotoSource({
    hasPhoto: listing.photos.length > 0,
    loadMode: photoLoadMode,
  });
  const photoUrl = buildPropertyPhotoUrl(listing.photos[0], {
    preset: '3:2-md',
    presetScale: '2/3',
    dpr: '1',
  });

  const query = new URLSearchParams({
    ...(utmSource && { utm_source: utmSource }),
    ...(utmMedium && { utm_medium: utmMedium }),
    ...(utmCampaign && { utm_campaign: utmCampaign }),
    ...(utmContent && { utm_content: utmContent }),
    ...(utmTerm && { utm_term: utmTerm }),
  }).toString();
  const propertyLink = corePdpLink(listing.slug ?? '_', listing.addressToken);
  const link = `${propertyLink}${query ? `?${query}` : ''}`;

  const { primaryLine, secondaryLine } = buildAddressLines(listing);

  // Photography gates going live, so available-soon homes are photo-less by
  // construction — street view stands in when we have the geo point.
  const isAvailableSoon = listing.listingState === 'AVAILABLE_SOON';
  const streetViewLocation = streetViewLocationFor({
    availableSoon: isAvailableSoon,
    photoCount: listing.photos.length,
    latitude: listing.latitude,
    longitude: listing.longitude,
  });

  let photoMedia: React.ReactElement | undefined;
  if (streetViewLocation) {
    photoMedia = (
      <CardLink
        href={link}
        external={isSearchResult}
        ariaLabel="Main view of property"
        analyticsName="marketplace-property-card-image-link"
        className="h-full w-full"
      >
        <StreetViewPhoto
          latitude={streetViewLocation.latitude}
          longitude={streetViewLocation.longitude}
          alt={`Street view of ${listing.fullAddress ?? 'this home'}`}
          className="aspect-[16/9] w-full"
          fallbackLabel="Photos coming soon"
        />
      </CardLink>
    );
  } else if (photoMode === 'gallery' || isPdpRefresh) {
    photoMedia = (
      <PropertyCardGallery
        key={listing.addressToken}
        variant={isPdpRefresh ? 'pdp-refresh' : 'default'}
        addressId={listing.addressToken}
        href={link}
        isPhotoPending={isPhotoPending}
        openInNewTab={isSearchResult}
        placeholderLabel={isAvailableSoon ? 'Photos coming soon' : undefined}
        previewPhotoUrls={shouldLoadPhoto ? listing.photos : EMPTY_PHOTO_URLS}
        fetchPriority={photoLoadMode === 'priority' ? 'high' : undefined}
      />
    );
  }

  const listingStatus = displayBadge ? browseListingStatus(displayBadge) : null;
  const showListingStatus = isSearchResult
    ? listingStatus === 'coming-soon' || listingStatus === 'self-tour'
    : listingStatus != null;
  const handleOpenShareModal = useCallback(() => setShareModalState('open'), []);
  const handleCloseShareModal = useCallback(() => setShareModalState('closed'), []);
  const shareableProperty = useMemo<ShareableProperty>(
    () => ({
      addressToken: listing.addressToken,
      slug: listing.slug ?? '_',
      street: listing.street ?? null,
      unitNumber: null,
      unitType: null,
    }),
    [listing.addressToken, listing.slug, listing.street],
  );

  return (
    <Col ref={containerRef} className={contained ? 'h-full w-full' : 'w-full'}>
      {isPdpRefresh ? (
        <PdpSimilarListingCard
          listing={listing}
          href={link}
          media={photoMedia}
          isLiked={isLiked}
          isFavoriteLoading={isFavoriteLoading}
          onToggleFavorite={onToggleFavorite}
          onShare={handleOpenShareModal}
        />
      ) : (
        <PropertyCard
          contained={contained}
          baths={listing.baths}
          beds={listing.beds}
          city={secondaryLine}
          dwellingType={convertDwellingType(listing.dwellingType)}
          href={link}
          openLinksInNewTab={isSearchResult}
          isFavorite={isLiked}
          isFavoriteLoading={isFavoriteLoading}
          neighborhood={primaryLine}
          onFavoritePress={onToggleFavorite}
          onSharePress={isSearchResult ? handleOpenShareModal : undefined}
          isPhotoPending={isPhotoPending}
          photoMedia={photoMedia}
          photoOverlay={
            displayBadge && showListingStatus ? (
              <ListingPhotoOverlay displayBadge={displayBadge} />
            ) : undefined
          }
          photoUrl={shouldLoadPhoto ? photoUrl : null}
          photoFetchPriority={photoLoadMode === 'priority' ? 'high' : undefined}
          price={listing.listPrice}
          selfTourAvailable={displayBadge === undefined ? listing.canSelfTour : false}
          statusLabel={isAvailableSoon ? 'Available soon' : null}
          sqft={listing.livingSquareFootage}
        />
      )}
      {(isSearchResult || isPdpRefresh) && shareModalState !== 'unopened' ? (
        <ShareModal
          property={shareableProperty}
          displayAddress={listing.fullAddress}
          isOpen={shareModalState === 'open'}
          onClose={handleCloseShareModal}
          trackingSource="gallery"
        />
      ) : null}
    </Col>
  );
};

const SimilarListingsContent: React.FC<SimilarListingsProps & Tracking> = ({
  listings,
  title,
  wide = false,
  utmSource,
  utmMedium,
  utmCampaign,
  utmContent,
  utmTerm,
}) => {
  const displayedListingSetCount = wide ? 8 : 6;
  const [displayedListingCount, setDisplayedListingCount] =
    React.useState(displayedListingSetCount);

  const incrementDisplayedListingCount = useCallback(() => {
    setDisplayedListingCount((prev) => prev + displayedListingSetCount);
  }, [displayedListingSetCount]);

  const similarListings = listings.slice(0, displayedListingCount).map((listing) => (
    <Col key={listing.addressToken} className={wide ? 'min-w-0' : 'w-full pb-6 lg:w-[48%]'}>
      <ListingCard
        listing={listing}
        variant={wide ? 'pdp-refresh' : 'default'}
        contained={wide}
        utmSource={utmSource}
        utmMedium={utmMedium}
        utmCampaign={utmCampaign}
        utmContent={utmContent}
        utmTerm={utmTerm}
      />
    </Col>
  ));

  return (
    <Col className={wide ? 'w-full' : 'pb-31 md:pb-6'}>
      <Text
        as={wide ? 'h2' : 'h5'}
        className={
          wide
            ? 'pb-5 text-label-lg leading-[22px] text-[#231e1b]'
            : 'pb-6 text-subheader-xs text-od-content-primary'
        }
      >
        {title || 'Similar listings'}
      </Text>
      <Col
        className={
          wide
            ? 'grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4'
            : 'flex-wrap justify-between lg:flex-row'
        }
      >
        {similarListings}
      </Col>
      {!wide && displayedListingCount < listings.length && (
        <Button
          onClick={incrementDisplayedListingCount}
          variant="secondary"
          size="lg"
          analyticsName="marketplace-show-more-listings"
          aria-label="See more homes"
          className="w-full"
        >
          See more homes
        </Button>
      )}
    </Col>
  );
};

const SimilarListingsWithGallery = (props: SimilarListingsProps & Tracking) => {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: { queries: { retry: 1 } },
        queryCache: createMarketplaceQueryCache(),
      }),
  );
  useEffect(() => () => queryClient.clear(), [queryClient]);
  return (
    <QueryClientProvider client={queryClient}>
      <SimilarListingsContent {...props} />
    </QueryClientProvider>
  );
};

export const SimilarListings = (props: SimilarListingsProps & Tracking) =>
  props.wide ? <SimilarListingsWithGallery {...props} /> : <SimilarListingsContent {...props} />;
